import { Badge } from "../../badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../select";
import { PipelineEditor } from "../settings/pipeline-editor";
import { apiCall } from "../../api/client";
import {
  hiringPipelines,
  type PipelineShape,
  type StageShape,
  type StageKind,
} from "../../api/contracts/hiring-pipelines";

interface Props {
  pipelines: PipelineShape[];
}

const STAGE_KINDS: StageKind[] = [
  "lead",
  "active",
  "offer",
  "hired",
  "rejected",
];

export function PipelineSettings({ pipelines }: Props) {
  return (
    // Pin the generics: PipelineEditor can't infer the stage type S from the
    // pipelines prop alone (it appears only in the constraint + the callback
    // params, neither an inference site), so it falls back to the base
    // PipelineStageRow, which has no `kind`. Naming StageShape here gives the
    // render callbacks the real hiring stage type.
    <PipelineEditor<StageShape, PipelineShape>
      title="Pipelines"
      description="A pipeline defines the stages a candidate moves through, end to end."
      pipelines={pipelines}
      newStageDefaults={{ kind: "active" satisfies StageKind }}
      allowSetDefault={false}
      renderStageBadge={(stage) => {
        const kind = stage.kind;
        return (
          <Badge variant="outline" className="capitalize">
            {kind}
          </Badge>
        );
      }}
      renderNewStageExtras={(extras, setExtras) => (
        <Select
          value={(extras.kind as StageKind | undefined) ?? "active"}
          onValueChange={(v) => setExtras({ kind: v as StageKind })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAGE_KINDS.map((k) => (
              <SelectItem key={k} value={k}>
                <span className="capitalize">{k}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      onPipelineCreate={async ({ slug, name }) => {
        await apiCall(hiringPipelines.create, { body: { slug, name } });
      }}
      onStageCreate={async (pipelineId, draft) => {
        await apiCall(hiringPipelines.stagesAdd, {
          params: { id: pipelineId },
          body: {
            slug: draft.slug,
            name: draft.name,
            kind: (draft.extras.kind as StageKind) ?? "active",
          },
        });
      }}
      onStageUpdate={async (pipelineId, stageId, patch) => {
        await apiCall(hiringPipelines.stagesUpdate, {
          params: { id: pipelineId, stageId },
          body: patch,
        });
      }}
      onStageArchive={async (pipelineId, stageId) => {
        await apiCall(hiringPipelines.stagesArchive, {
          params: { id: pipelineId, stageId },
        });
      }}
    />
  );
}
